import { trackGAEvent } from '@/utility/analytics';
import {
  MONEY_PAGE_CLICKED_CREATE_PAID_EVENT,
  MONEY_PAGE_CLICKED_OPEN_EDIT_MEMBERSHIP_MODAL,
  MONEY_PAGE_CLICKED_OPEN_EDIT_REVENUE_TARGET_MODAL,
  MONEY_PAGE_CLICKED_PAYOUT_FORM,
  MONEY_PAGE_EDIT_COMMUNITY_PRICE_EVENT,
  MONEY_PAGE_EDIT_REVENUE_TARGET_SUCCESS_EVENT
} from '@/utility/analyticsConsts';
import { formatDateTimeWithLocale, t } from '@/utility/localization';

import { getFullDiscountText } from '@/components/common/DiscountCreationDisplay/util';
import NextImage from '@/components/common/NextImage';
import NplToolTip from '@/components/common/NplToolTip';
import Icon from '@/components/npl/Icon';

import { TYPES_OF_PAYMENT_METHODS } from '../settings/Components/PriceSetting/constants';
import getConstants, { getMoneyTabs } from './consts';

export const getCommunityPaymentType = (community) => {
  if (community?.isPaidCommunity) return TYPES_OF_PAYMENT_METHODS.paid;
  if (community?.isTokenGated) return TYPES_OF_PAYMENT_METHODS.NFT;

  return TYPES_OF_PAYMENT_METHODS.free;
};

const _trackMonePageEvent = (actionId, user, community) => {
  const params = {
    email: user?.email,
    communityId: community?._id
  };

  trackGAEvent(actionId, params);
};

export const trackClickedCreatePaidEvent = (user, community) =>
  _trackMonePageEvent(
    MONEY_PAGE_CLICKED_CREATE_PAID_EVENT,
    user,
    community
  );

export const trackClickedOpenEditMembershipModal = (user, community) =>
  _trackMonePageEvent(
    MONEY_PAGE_CLICKED_OPEN_EDIT_MEMBERSHIP_MODAL,
    user,
    community
  );

export const trackEditCommunityPriceSucces = (user, community) =>
  _trackMonePageEvent(
    MONEY_PAGE_EDIT_COMMUNITY_PRICE_EVENT,
    user,
    community
  );

export const trackClickedPayoutForm = (user, community) =>
  _trackMonePageEvent(MONEY_PAGE_CLICKED_PAYOUT_FORM, user, community);

export const trackClickedOpenRevenueTargetModal = (user, community) =>
  _trackMonePageEvent(
    MONEY_PAGE_CLICKED_OPEN_EDIT_REVENUE_TARGET_MODAL,
    user,
    community
  );

export const trackEditRevenueTargetSuccess = (user, community) =>
  _trackMonePageEvent(
    MONEY_PAGE_EDIT_REVENUE_TARGET_SUCCESS_EVENT,
    user,
    community
  );

export const renderTableIcon = (transaction) => {
  const { transactionTypes } = getConstants();
  switch (transaction?.purchaseType) {
    case transactionTypes.MEMBERSHIP:
      return (
        <div className="flex h-32 w-32 flex-row items-center justify-center rounded-full bg-npl-success-light-3">
          <Icon name="users-01" fill="#18774C" height="16" width="16" />
        </div>
      );

    case transactionTypes.FOLDER:
      return (
        <div className="flex h-32 w-32 flex-row items-center justify-center rounded-full bg-npl-blurple-light-3">
          <Icon name="file-01" fill="#3442B2" height="16" width="16" />
        </div>
      );

    case transactionTypes.EVENT:
      return (
        <div className="flex h-32 w-32 flex-row items-center justify-center rounded-full bg-npl-yellow-light-solid-3">
          <Icon name="calendar" fill="#946800" height="16" width="16" />
        </div>
      );

    case transactionTypes.REFERRAL_BONUS:
      return (
        <div className="flex h-32 w-32 flex-row items-center justify-center rounded-full bg-npl-pink-light-3">
          <Icon
            name="stars-filled-01"
            fill="#CD1D8D"
            height="16"
            width="16"
          />
        </div>
      );
    case transactionTypes.CAMPAIGN_REWARD:
      return (
        <div className="flex h-32 w-32 flex-row items-center justify-center rounded-full bg-npl-pink-light-3">
          <Icon name="gift-01" fill="#CD1D8D" height="16" width="16" />
        </div>
      );
    case transactionTypes.SESSION:
      return (
        <div className="flex h-32 w-32 flex-row items-center justify-center rounded-full bg-npl-blurple-light-3">
          <Icon name="1-1-session" fill="#3442B2" height="16" width="16" />
        </div>
      );
    case transactionTypes.CHALLENGE:
      return (
        <div className="flex h-32 w-32 flex-row items-center justify-center rounded-full bg-npl-orange-light-3">
          <Icon name="trophy-01" fill="#BD4B00" height="16" width="16" />
        </div>
      );
    case transactionTypes.ADJUSTMENT_DEDUCT:
    case transactionTypes.ADJUSTMENT_ADD:
      return (
        <div className="flex h-32 w-32 flex-row items-center justify-center rounded-full bg-npl-pink-light-3">
          <Icon
            name="currency-dollar"
            fill="#CD1D8D"
            height="16"
            width="16"
          />
        </div>
      );
  }
};

export const renderOneTimeTableThumbnail = (product) => {
  if (!product?.thumbnailUrl) {
    return null;
  }

  const imgProps = {
    alt: product?.title,
    mobileImgProps: {
      src: product?.thumbnailUrl,
      layout: 'fill',
      objectFit: 'cover',
      objectPosition: 'center',
      width: 100,
      height: 56
    },
    desktopImgProps: {
      src: product?.thumbnailUrl,
      layout: 'fill',
      objectFit: 'cover',
      objectPosition: 'center',
      width: 64,
      height: 36
    }
  };

  return (
    <div className="relative h-[56px] w-[100px] overflow-hidden rounded-4 md:h-[36px] md:w-[64px]">
      <NextImage {...imgProps} />
    </div>
  );
};

export const getEntityDisplayName = (entityType) => {
  switch (entityType) {
    case 'EVENT':
      return t('event2');
    case 'FOLDER':
      return t('digital-product');
    case 'SESSION':
      return t('1-1-session-0');
    case 'CHALLENGE':
      return t('challenge');
  }
};

export const getRefundText = (status) => {
  if (!status) return null;

  switch (status) {
    case 'REQUESTED':
      return t('refund-requested');
    case 'REFUNDED':
      return t('refund-completed');
    default:
      return t('refund-requested');
  }
};

export const renderTableRowTitle = (transaction) => {
  const { transactionTypes } = getConstants();
  let isBonus =
    transaction?.purchaseType === transactionTypes.REFERRAL_BONUS ||
    transaction?.purchaseType === transactionTypes.CAMPAIGN_REWARD;
  let title = '';
  switch (transaction?.purchaseType) {
    case transactionTypes.EVENT:
      title = transaction?.eventData?.title;
      break;
    case transactionTypes.FOLDER:
      title = transaction?.folderData?.title;
      break;
    case transactionTypes.MEMBERSHIP:
      title = t('membership');
      break;
    case transactionTypes.REFERRAL_BONUS:
    case transactionTypes.CAMPAIGN_REWARD:
      title = transaction?.metadata?.title || t('cash-bonus');
      break;
    case transactionTypes.SESSION:
      title = transaction?.folderData?.title;
      break;
    case transactionTypes.CHALLENGE:
      title = transaction?.challengeData?.title;
      break;
    case transactionTypes.ADJUSTMENT_ADD:
    case transactionTypes.ADJUSTMENT_DEDUCT:
      title = t('balance-adjustment');
      break;
    default:
      title = t('digital-product');
  }

  let refundStatus =
    transaction?.transactionDetails?.refundDetails?.status;

  if (transaction?.purchaseType === transactionTypes.MEMBERSHIP) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center gap-10">
          <div className="text-label-md font-medium text-npl-text-icon-on-light-surface-primary">
            {t('membership')}
          </div>
          {transaction?.transactionType === 'OUTBOUND' && (
            <div className="rounded-4 bg-npl-pink-light-3 px-4 text-label-xs text-npl-pink-light-11">
              {t('refund')}
            </div>
          )}
          {refundStatus && (
            <div
              className={`px-4 ${
                refundStatus === 'REQUESTED'
                  ? 'bg-npl-pink-light-3 text-npl-pink-light-11'
                  : 'bg-npl-neutral-light-solid-3 text-npl-neutral-light-solid-11 '
              } rounded-4  text-label-xs`}>
              {getRefundText(refundStatus)}
            </div>
          )}
        </div>
        <div className="text-label-sm text-npl-text-icon-on-light-surface-secondary">
          {transaction?.subscriptionData?.membershipType || 'Monthly'}{' '}
          {transaction?.originalDiscountAmount > 0 &&
            `- ${getFullDiscountText(transaction?.discountData)}`}
        </div>
      </div>
    );
  } else if (isBonus) {
    return (
      <div className="flex flex-col">
        <div className="flex flex-row items-center gap-10">
          <div className="text-label-md font-medium text-npl-text-icon-on-light-surface-primary">
            {title}
          </div>
          {transaction?.transactionType === 'OUTBOUND' &&
            transaction?.purchaseType !==
              transactionTypes.ADJUSTMENT_DEDUCT && (
              <div className="rounded-4 bg-npl-pink-light-3 px-4 text-label-xs text-npl-pink-light-11">
                {transaction?.metadata?.isChargeback
                  ? t('chargeback')
                  : t('refund')}
              </div>
            )}
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-row items-center gap-10">
          <div className="text-label-md font-medium text-npl-text-icon-on-light-surface-primary">
            {title}
          </div>
          {transaction?.transactionType === 'OUTBOUND' &&
            transaction?.purchaseType !==
              transactionTypes.ADJUSTMENT_DEDUCT && (
              <div className="rounded-4 bg-npl-pink-light-3 px-4 text-label-xs text-npl-pink-light-11">
                {transaction?.metadata?.isChargeback
                  ? t('chargeback')
                  : t('refund')}
              </div>
            )}
          {refundStatus && (
            <div
              className={`px-4 ${
                refundStatus === 'REQUESTED'
                  ? 'bg-npl-pink-light-3 text-npl-pink-light-11'
                  : 'bg-npl-neutral-light-solid-3 text-npl-neutral-light-solid-11 '
              } rounded-4 text-label-xs`}>
              {getRefundText(refundStatus)}
            </div>
          )}
        </div>
        {transaction?.originalDiscountAmount > 0 && (
          <div className="text-label-sm text-npl-text-icon-on-light-surface-secondary">
            {`${transaction?.discountData?.value}${t('discount1')}`}
          </div>
        )}
        {transaction?.eventData?.quantity &&
        transaction?.eventData?.quantity > 1 ? (
          <p className="text-label-sm text-dark-1b text-opacity-65">
            {t('x-tickets', {
              number: transaction?.eventData?.quantity
            })}
          </p>
        ) : null}
      </div>
    );
  }
};

export const getChartDataPointName = (dataDate, aggregationType) => {
  switch (aggregationType) {
    case 'DAILY':
      return `${formatDateTimeWithLocale(dataDate, 'dd MMM', true)}`;
    case 'MONTHLY':
      return formatDateTimeWithLocale(dataDate, 'MMMM', true);
    default:
      return `${formatDateTimeWithLocale(dataDate, 'dd MMM', true)}`;
  }
};

// Here we take Data and format it for the chart, setting up the chart config also
export const getFormattedRechartConfig = (data) => {
  const formatedData = [];
  const { graph, aggregationType } = data || {};

  if (!graph || graph?.length === 0) return formatedData;

  graph?.forEach((item) => {
    const dataDate = new Date(item?.date);

    // Only set label for first and last day of month
    // if (index === 0 || index === graph?.length - 1) {
    //   formatedData.options.xaxis.categories.push(
    //     `${dataDate.getDate()} ${getMonthName(dataDate.getMonth(), true)}`
    //   );
    // } else {
    //   formatedData.options.xaxis.categories.push('');
    // }

    const totalOneTimeRevenue =
      (item?.folders?.amount || 0) +
      (item?.events?.amount || 0) +
      (item?.sessions?.amount || 0) +
      (item?.challenges?.amount || 0);

    let chartDataObj = {
      name: getChartDataPointName(dataDate, aggregationType),
      date: item?.date
    };

    // check if date is before day of current month (We only show expected revenue for future dates) - previous months will show all data
    if (dataDate > new Date()) {
      chartDataObj['Existing Memberships (Upcoming)'] =
        item?.expectedSubscriptions?.totalAmount;
      // only round the border of the last bar
      chartDataObj['roundedBorderOn'] = 'Existing Memberships (Upcoming)';
    } else {
      chartDataObj['One-time Purchase'] = totalOneTimeRevenue;
      chartDataObj['New Memberships'] = item?.subscriptions?.newAmount;
      chartDataObj['Gift from Nas.io'] = item?.campaignRewards?.amount;
      chartDataObj['Existing Memberships (Received)'] =
        item?.subscriptions?.existingAmount;
      // only round the border of the last bar
      chartDataObj['roundedBorderOn'] =
        totalOneTimeRevenue > 0
          ? 'Existing Memberships (Received)'
          : item?.subscriptions?.newAmount > 0
            ? 'New Memberships'
            : 'Existing Memberships (Received)';
    }

    chartDataObj['total'] = item?.total?.amount;
    formatedData.push(chartDataObj);
  });
  return formatedData;
};

export function daysInThisMonth(month) {
  var now = new Date();
  return new Date(now.getFullYear(), month + 1, 0).getDate();
}

// This function will return the last day of the month in format YYYY-MM-DD
export function getFirstDayOfNextMonthFromYearAndMonth(year, month) {
  let nextMonthYear = year;
  let nextMonthMonth = month + 1;

  if (nextMonthMonth > 11) {
    nextMonthYear++;
    nextMonthMonth = 0;
  }

  return `${nextMonthYear}-${String(nextMonthMonth + 1).padStart(
    2,
    '0'
  )}-01T00:00:00.000Z`;
}

// This function will return the last day of the month in format YYYY-MM-DD
export function getLastDayOfMonthFromYearAndMonth(year, month) {
  const lastDayOfMonth = new Date(year, month + 1, 0);

  return `${year}-${String(month + 1).padStart(2, '0')}-${String(
    lastDayOfMonth?.getDate()
  ).padStart(2, '0')}T23:59:59.999Z`;
}

// This function will return the last day of the month in format YYYY-MM-DD
export function getDateOfNextDay(date) {
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);
  return nextDay.toISOString();
}

// Get time interval to fetch earnings for (dont use new Date because it will use local timezone)
export function getStartEndDateForCurrentDateMonth(date) {
  const firstDayOfMonth = `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, '0')}-01T00:00:00.000Z`;
  const lastDayOfMonth = getLastDayOfMonthFromYearAndMonth(
    date.getFullYear(),
    date.getMonth()
  );

  return {
    startDate: firstDayOfMonth,
    endDate: lastDayOfMonth
  };
}

// Get time interval to fetch earnings for (dont use new Date because it will use local timezone)
export function getStartEndDateFromYearAndMonth(year, month) {
  const firstDayOfMonth = `${year}-${String(month + 1).padStart(
    2,
    '0'
  )}-01T00:00:00.000Z`;
  const lastDayOfMonth = getLastDayOfMonthFromYearAndMonth(year, month);

  return {
    startDate: firstDayOfMonth,
    endDate: lastDayOfMonth
  };
}

export function routeToTransactionsTabWithQueryDates(
  router,
  startDate,
  endDate
) {
  router.push({
    pathname: '/portal/money',
    query: {
      tab: 'Transactions',
      startDate,
      endDate
    }
  });
}

export const getPayoutStatusBadge = (status) => {
  switch (status) {
    case 'Paid':
      return (
        <div className="w-fit rounded-4 bg-npl-success-light-3 px-8 py-2 text-label-sm font-medium text-npl-success-light-11">
          {t('paid')}
        </div>
      );
    case 'Processing':
      return (
        <div className="flex h-fit w-fit flex-row items-center gap-6 rounded-4 bg-npl-neutral-light-solid-3 px-8 py-2">
          <div className="text-label-sm text-npl-text-icon-on-light-surface-secondary">
            {t('processing1')}
          </div>
          <NplToolTip
            title={t('processing')}
            text={t(
              'payouts-are-typically-processed-between-5-7-working-days-after-the-month-has-concluded-once-processed-the-funds-can-take-up-to-1-5-days-to-appear-in-your-bank-account'
            )}>
            <Icon
              name="info-circle"
              height="16"
              width="16"
              fill="#1B1B1880"
            />
          </NplToolTip>
        </div>
      );
    default:
      return <></>;
  }
};

export const gePayoutContactUsLink = (
  communityName,
  communityCode,
  payoutId
) => {
  return `mailto:finance@nas.io?body=Community%20Name%3A%20${communityName}%20(${communityCode})%0A${
    payoutId
      ? `PayoutID%3A%20${payoutId}%0A`
      : 'PayoutID%3A%20Payout%20Pending%0A'
  }Date%20%26%20Time%3A%20${encodeURIComponent(
    new Date().toLocaleString()
  )}%0A%0A(Tell%20us%20how%20we%20can%20help%20by%20describing%20your%20problem%20and%20uploading%20any%20relevant%20screenshots%20or%20attachments)`;
};

// For new fields added to payoutInfo, return the field to fill for new countries
export const getFieldsToFill = (payoutInfo) => {
  // Means user already filled in updated payoutInfo as bankCountryCode was only added with new changes
  if (!payoutInfo?.bankCountryCode) return;

  switch (payoutInfo?.bankCountryCode) {
    case 'BR':
      if (
        payoutInfo?.accountType === 'individual' &&
        !payoutInfo?.cpfNumber
      )
        return 'cpfNumber';
      if (payoutInfo?.accountType === 'company' && !payoutInfo?.cnpjNumber)
        return 'cnpjNumber';
      break;
    case 'MX':
      if (!payoutInfo?.clabe) return 'clabe';
      break;
    case 'CA':
      if (!payoutInfo?.transitCode) return 'transitCode';
      else if (!payoutInfo?.institutionNumber) return 'institutionNumber';
      break;
    default:
      return null;
  }

  // return false;
};

export const getAccessibleMoneyTabs = (user) => {
  const moneyTabs = getMoneyTabs();

  if (user?.activeCommunityRoles?.includes('owner')) {
    return moneyTabs;
  }

  return moneyTabs.filter((tab) => tab.checkAccessWithUserData(user));
};
